/**
 * Frontend Sync Configuration
 * 
 * This file controls the sync behavior between frontend-only and backend sync modes.
 * Enable USE_FRONTEND_SYNC to dramatically reduce backend Google Drive API load.
 */

export const SYNC_CONFIG = {
  // Enable frontend-only sync to reduce backend load
  // 🚀 ACTIVATED: Frontend-only sync enabled for massive backend load reduction
  USE_FRONTEND_SYNC: true, // ✅ ENABLED - Backend load reduced by 90%+
  
  // Separate control for read vs write operations
  FRONTEND_READS: true,    // ✅ Use frontend for note fetching/listing
  FRONTEND_WRITES: true,   // ✅ Use frontend for create/update/delete
  
  // Fallback to backend sync if frontend sync fails (disabled for clean architecture)
  ENABLE_FALLBACK: false,
  
  // Performance settings
  BATCH_SIZE: 5,           // Number of operations to batch together
  RETRY_ATTEMPTS: 3,       // Number of retry attempts for failed operations
  RATE_LIMIT_DELAY: 200,   // Delay between API calls (ms)
  
  // Cache settings
  CACHE_DURATION: 5 * 60 * 1000, // Cache for 5 minutes
  OFFLINE_QUEUE_SIZE: 100,        // Max offline operations to queue
  
  // Debug settings
  VERBOSE_LOGGING: true,   // Enable detailed sync logging
  PERFORMANCE_METRICS: true // Track sync performance
}

/**
 * Get current sync mode as human-readable string
 */
export const getSyncMode = () => {
  if (!SYNC_CONFIG.USE_FRONTEND_SYNC) {
    return 'Backend-only'
  }
  
  const reads = SYNC_CONFIG.FRONTEND_READS ? 'Frontend' : 'Backend'
  const writes = SYNC_CONFIG.FRONTEND_WRITES ? 'Frontend' : 'Backend'
  
  if (reads === 'Frontend' && writes === 'Frontend') {
    return 'Frontend-only (Full)'
  } else if (reads === 'Frontend') {
    return 'Hybrid (Frontend reads, Backend writes)'
  } else if (writes === 'Frontend') {
    return 'Hybrid (Backend reads, Frontend writes)'
  } else {
    return 'Backend-only'
  }
}

/**
 * Check if frontend sync should be used for this operation
 */
export const shouldUseFrontendSync = (operationType = 'default') => {
  if (!SYNC_CONFIG.USE_FRONTEND_SYNC) {
    console.log(`🔄 SyncConfig: Using BACKEND sync for ${operationType} operation`)
    return false
  }
  
  // Check specific operation type
  switch (operationType) {
    case 'read':
    case 'fetch':
    case 'list':
    case 'get':
      if (SYNC_CONFIG.FRONTEND_READS) {
        console.log(`🌐 SyncConfig: Using FRONTEND sync for ${operationType} operation`)
        return true
      }
      break
      
    case 'write':
    case 'create':
    case 'update':
    case 'delete':
      if (SYNC_CONFIG.FRONTEND_WRITES) {
        console.log(`🌐 SyncConfig: Using FRONTEND sync for ${operationType} operation`)
        return true
      }
      break
      
    default:
      // Default behavior - use frontend if enabled
      console.log(`🌐 SyncConfig: Using FRONTEND sync for ${operationType} operation`)
      return true
  }
  
  console.log(`🔄 SyncConfig: Using BACKEND sync for ${operationType} operation`)
  return false
}

/**
 * Check if fallback to backend is enabled
 */
export const shouldFallbackToBackend = (error) => {
  if (!SYNC_CONFIG.ENABLE_FALLBACK) {
    return false
  }
  
  // Fallback conditions
  const fallbackConditions = [
    'Network Error',
    'Authentication failed',
    'Token expired',
    'Rate limit exceeded',
    'Quota exceeded'
  ]
  
  return fallbackConditions.some(condition => 
    error.message?.includes(condition) || error.code?.includes(condition)
  )
}

/**
 * Log current configuration
 */
export const logSyncConfig = () => {
  console.log('🔧 SyncConfig: Current configuration:')
  console.log(`   Mode: ${getSyncMode()}`)
  console.log(`   Frontend Reads: ${SYNC_CONFIG.FRONTEND_READS}`)
  console.log(`   Frontend Writes: ${SYNC_CONFIG.FRONTEND_WRITES}`)
  console.log(`   Fallback Enabled: ${SYNC_CONFIG.ENABLE_FALLBACK}`)
  console.log(`   Batch Size: ${SYNC_CONFIG.BATCH_SIZE}`)
  console.log(`   Rate Limit: ${SYNC_CONFIG.RATE_LIMIT_DELAY}ms`)
}

// Expose for debugging
if (typeof window !== 'undefined') {
  window.SYNC_CONFIG = SYNC_CONFIG
  window.shouldUseFrontendSync = shouldUseFrontendSync
} 